import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { AppRole, AreaRow } from './database.types';
import type { Grants } from './rbac';

interface OrgMembership {
  orgId: string;
  orgName: string;
  role: AppRole;
}

interface AuthState {
  loading: boolean;
  session: Session | null;
  userId: string | null;
  email: string | null;
  fullName: string | null;
  orgs: OrgMembership[];
  currentOrgId: string | null;
  setCurrentOrgId: (id: string) => void;
  areas: AreaRow[]; // áreas da org atual
  grants: Grants | null; // papel + áreas EFETIVOS (respeita o modo admin)
  isRealAdmin: boolean; // admin de verdade (independe do modo)
  adminMode: boolean; // admin vendo como admin (true) ou como usuário de área (false)
  setAdminMode: (on: boolean) => void;
  mustChangePassword: boolean; // 1º acesso: senha provisória precisa ser trocada
  signInPassword: (email: string, password: string) => Promise<{ error?: string }>;
  signInMagicLink: (email: string) => Promise<{ error?: string }>;
  changePassword: (newPassword: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);
const ORG_KEY = 'npd.currentOrgId';

/** PostgREST retorna relações "to-one" como objeto em runtime, mas o cliente
 *  sem tipos gerados as declara como array. Normaliza para um único registro. */
function rel<T>(v: unknown): T | null {
  if (Array.isArray(v)) return (v[0] as T) ?? null;
  return (v as T) ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [orgs, setOrgs] = useState<OrgMembership[]>([]);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(
    () => localStorage.getItem(ORG_KEY),
  );
  const [areas, setAreas] = useState<AreaRow[]>([]);
  const [userAreaKeys, setUserAreaKeys] = useState<string[]>([]);
  const [adminMode, setAdminModeState] = useState<boolean>(() => {
    try {
      return localStorage.getItem('npd.adminMode') !== 'off';
    } catch {
      return true;
    }
  });
  const setAdminMode = (on: boolean) => {
    try {
      localStorage.setItem('npd.adminMode', on ? 'on' : 'off');
    } catch {
      /* ignore */
    }
    setAdminModeState(on);
  };

  const setCurrentOrgId = (id: string) => {
    localStorage.setItem(ORG_KEY, id);
    setCurrentOrgIdState(id);
  };

  // Sessão
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Perfil + organizações do usuário
  useEffect(() => {
    if (!session?.user) {
      setOrgs([]);
      setFullName(null);
      setMustChangePassword(false);
      return;
    }
    const uid = session.user.id;
    (async () => {
      const { data: prof } = await supabase
        .from('profiles')
        .select('full_name, must_change_password')
        .eq('id', uid)
        .maybeSingle();
      setFullName(prof?.full_name ?? session.user.email ?? null);
      setMustChangePassword(Boolean(prof?.must_change_password));

      const { data: mems } = await supabase
        .from('memberships')
        .select('org_id, role, organizations(name)')
        .eq('user_id', uid);

      const list: OrgMembership[] = ((mems ?? []) as unknown[]).map((row) => {
        const m = row as { org_id: string; role: AppRole; organizations: unknown };
        return {
          orgId: m.org_id,
          role: m.role,
          orgName: rel<{ name: string }>(m.organizations)?.name ?? 'Organização',
        };
      });
      setOrgs(list);
      if (list.length && (!currentOrgId || !list.some((o) => o.orgId === currentOrgId))) {
        setCurrentOrgId(list[0]!.orgId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // Áreas da org atual + áreas do usuário
  useEffect(() => {
    if (!session?.user || !currentOrgId) {
      setAreas([]);
      setUserAreaKeys([]);
      return;
    }
    const uid = session.user.id;
    (async () => {
      const { data: allAreas } = await supabase
        .from('areas')
        .select('id, org_id, key, name')
        .eq('org_id', currentOrgId)
        .order('name');
      setAreas((allAreas as AreaRow[]) ?? []);

      const { data: mem } = await supabase
        .from('memberships')
        .select('id')
        .eq('org_id', currentOrgId)
        .eq('user_id', uid)
        .maybeSingle();

      if (mem?.id) {
        const { data: ma } = await supabase
          .from('member_areas')
          .select('areas(key)')
          .eq('membership_id', mem.id);
        setUserAreaKeys(
          (((ma ?? []) as unknown[])
            .map((r) => rel<{ key: string }>((r as { areas: unknown }).areas)?.key)
            .filter(Boolean) as string[]),
        );
      } else {
        setUserAreaKeys([]);
      }
    })();
  }, [session?.user, currentOrgId]);

  const om = orgs.find((o) => o.orgId === currentOrgId);
  const isRealAdmin = om?.role === 'admin';

  const grants: Grants | null = useMemo(() => {
    if (!om) return null;
    // Modo admin desligado: o admin passa a ver/editar como um EDITOR das suas
    // próprias áreas (visão de usuário responsável). Não altera o servidor (RLS).
    if (om.role === 'admin' && !adminMode) {
      return { role: 'editor', areaKeys: userAreaKeys };
    }
    return { role: om.role, areaKeys: userAreaKeys };
  }, [om, adminMode, userAreaKeys]);

  const value: AuthState = {
    loading,
    session,
    userId: session?.user?.id ?? null,
    email: session?.user?.email ?? null,
    fullName,
    orgs,
    currentOrgId,
    setCurrentOrgId,
    areas,
    grants,
    isRealAdmin: Boolean(isRealAdmin),
    adminMode,
    setAdminMode,
    mustChangePassword,
    signInPassword: async (email, password) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error?.message };
    },
    signInMagicLink: async (email) => {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      return { error: error?.message };
    },
    changePassword: async (newPassword) => {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { error: error.message };
      const uid = session?.user?.id;
      if (uid) {
        await supabase.from('profiles').update({ must_change_password: false }).eq('id', uid);
      }
      setMustChangePassword(false);
      return {};
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
